// The tier ladder, and the one place on the page that knows how far along it the
// buyer is. The bar used to be part of the builder and could only be read there;
// now the sum lives here, the cart is always part of it, and whoever wants to
// draw it — the bar under a price, the bar above the builder, the builder's own
// rail — asks the same object and gets the same answer.

const buildBundleLadder = () => {
  const readPayload = (selector) =>
    utils.JSONParse(document.querySelector(selector)?.innerHTML) ?? {};

  const ladder = readPayload("[data-bundle-ladder-data]");

  // Prices out of Liquid already arrive in the customer's currency, but a
  // number_decimal metafield is a plain number the merchant typed in shop
  // currency. Comparing the two unconverted lets a buyer on a weak exchange rate
  // unlock tiers for a fraction of the money, so the thresholds are moved into
  // the same currency as the prices. Rate is a string, hence the Number().
  const currencyRate = Number(window.Shopify?.currency?.rate) || 1;
  const toLocalCurrency = (amount) => Math.round((amount ?? 0) * currencyRate);

  const tiers = (ladder.tiers ?? []).map((tier) => ({
    ...tier,
    threshold: toLocalCurrency(tier.threshold),
  }));
  const freeShippingPercent = Number(ladder.free_shipping_percent) || 0;

  // The whole point of moving the ladder to the shop: what counts toward a tier
  // is one list the merchant keeps in one place. A product outside it never
  // moves the bar, and a product inside it always does — an accessory as much as
  // a bench, and wherever the buyer put it.
  const thresholdProductIds = new Set(ladder.threshold_product_ids ?? []);

  const giftOptionsById = new Map();
  tiers.forEach((tier) =>
    (tier.gift_options ?? []).forEach((option) => giftOptionsById.set(option.id, option))
  );

  const giftTierCount = tiers.filter((tier) => (tier.gift_options ?? []).length > 0).length;
  const showsGiftTierBadge = giftTierCount > 1;

  // TEMPORARY, FOR TESTING. Every line the ladder has a say over carries a flag,
  // and the discount function refuses to work on a cart that has none. The
  // bundle metafields already sit on the live products, so without the gate the
  // function would start cutting prices for anyone who happened to buy the same
  // things from the current theme.
  //
  // It used to ride on the bench alone, because the bench was the only way in.
  // The bar is on every threshold product now, and a buyer can earn a tier
  // without the bench ever being in the cart, so the flag has to travel with
  // whatever put them over the line — an add-on bought from its own page as much
  // as a rig built in the builder.
  //
  // The leading underscore keeps it out of the cart and the checkout.
  // Remove this together with the matching check in the function.
  const BUNDLE_FLAG_KEY = "_bundle";

  const takesPartInTheLadder = (productId) =>
    thresholdProductIds.has(productId) || giftOptionsById.has(productId);

  const countsTowardThresholds = takesPartInTheLadder;

  const countsTowardDiscount = (productId) => thresholdProductIds.has(productId);

  const bundleProperties = (productId) =>
    takesPartInTheLadder(productId) ? { [BUNDLE_FLAG_KEY]: "true" } : {};

  const GIFT_CHOICE_KEY = "_bundle_gift";
  const GIFT_CHOICE_ADDED = "added";
  const GIFT_CHOICE_KEPT = "kept";

  const isChosenGiftLine = (line) => !!line?.properties?.[GIFT_CHOICE_KEY];

  const isAddedOnlyAsGift = (line) => line?.properties?.[GIFT_CHOICE_KEY] === GIFT_CHOICE_ADDED;

  const giftChoiceProperties = (isBoughtToo) => ({
    [GIFT_CHOICE_KEY]: isBoughtToo ? GIFT_CHOICE_KEPT : GIFT_CHOICE_ADDED,
  });

  const removeGiftChoice = (properties) =>
    Object.fromEntries(Object.entries(properties ?? {}).filter(([key]) => key !== GIFT_CHOICE_KEY));

  // A builder on the same page has items staged that are not in the cart yet.
  // They count toward the tier the moment they are ticked, or the bar would sit
  // still while the buyer fills the rig in front of it.
  //
  // Pages swap here without the browser reloading, so this store outlives the
  // page the builder was on. Left alone, a builder the buyer walked away from
  // keeps answering: the bar goes on staging gifts for a form that is no longer
  // on screen instead of putting them in the cart. Its element leaving the
  // document is the sign that it is no longer in charge, and it has to be read
  // at the moment of asking, because the outgoing page is still attached while
  // the incoming one starts up.
  let builder = null;
  let builderElement = null;
  const cartLines = () => window._stores?.cart?.state?.items ?? [];

  const sumCartLines = (keep) =>
    cartLines()
      .filter((line) => keep(line.product_id))
      .reduce((sum, line) => sum + (line.original_line_price ?? 0), 0);

  const cartSubtotal = () => sumCartLines(countsTowardThresholds);

  const getPageProductState = () => {
    const productState = window.Alpine.store("main_product")?.state;
    if (!productState?.element || !document.contains(productState.element)) return null;
    return productState;
  };

  const SINGLE_UNIT = 1;

  const buildProductStateLines = (productState, quantity = productState?.quantity || SINGLE_UNIT) => {
    if (!productState?.selected_variant) return [];
    const selectedLine = {
      product_id: productState.product?.id,
      variant_id: productState.selected_variant.id,
      price: (productState.selected_variant.price ?? 0) * quantity,
    };
    const addOnLines = [...(productState.upsell_items?.entries() ?? [])].map(([handle, addOn]) => ({
      product_id: addOn?.product_id ?? window._products?.[handle]?.id,
      variant_id: addOn?.id,
      price: (addOn?.price ?? 0) * quantity,
    }));
    return [selectedLine, ...addOnLines];
  };

  const getPageProductLines = () => {
    const variantIdsInCart = new Set(cartLines().map((line) => String(line.variant_id)));
    return buildProductStateLines(getPageProductState()).filter(
      (line) => !variantIdsInCart.has(String(line.variant_id))
    );
  };

  const sumPageProductLines = (keep) =>
    getPageProductLines()
      .filter((line) => keep(line.product_id))
      .reduce((sum, line) => sum + line.price, 0);

  const cartDiscountBase = () => sumCartLines(countsTowardDiscount);

  // The tier name and what the tier is worth are two separate wordings printed
  // back to back, so something has to put the gap between them. It cannot be a
  // leading space in the wording itself: the theme editor trims a text setting,
  // so a space typed there is gone the first time the merchant hits save.
  const spaceBefore = (fragment) => (fragment ? ` ${fragment.replace(/^\s+/, "")}` : "");

  // Nothing in the bundle is priced in cents, so the trailing ".00" is two
  // characters of noise — and on a phone, where the rail and the bar are already
  // fighting for room, two characters is a wrapped line.
  const money = (cents) => utils.formatMoney(cents).replace(/([.,])00(?!\d)/, "");

  // A tier percentage may read "free shipping" instead of a number. One entry in
  // the metafield covers every tier, which Liquid has already spread across them.
  const isFreeShippingValue = (value) =>
    String(value ?? "").trim().toLowerCase().replace(/[_-]+/g, " ") === "free shipping";

  const percentageOf = (index) => {
    const raw = tiers[index]?.percentage;
    return isFreeShippingValue(raw) ? 0 : Number(raw) || 0;
  };

  // Free shipping can sit on any tier and stacks with the percentages, so every
  // tier the buyer has reached is taken into account. Picking the largest percent
  // among them means spending more can never take a discount away — landing on a
  // free shipping tier would otherwise wipe out the percent earned below it.
  const reachedTiers = (tierIndex) => (tierIndex < 0 ? [] : tiers.slice(0, tierIndex + 1));

  const bestPercentage = (tierIndex) =>
    reachedTiers(tierIndex).reduce((best, tier, index) => Math.max(best, percentageOf(index)), 0);

  const hasFreeShipping = (tierIndex) =>
    reachedTiers(tierIndex).some((tier) => isFreeShippingValue(tier.percentage));

  // What the freight would have cost. It used to be looked up in a rate card by
  // country, which told the same order two different figures either side of a
  // border; it is a flat share of what the buyer is spending now.
  //
  // Rounded to whole money, because a share of an order lands on odd cents and
  // this is an estimate being offered, not a sum being charged. "About $197"
  // reads as the estimate it is; "$197.40" reads as a quote it is not.
  const shippingValueFor = (subtotal) =>
    Math.round((subtotal * freeShippingPercent) / 10000) * 100;

  const percentSavingFor = (subtotal, percentage) => Math.round((subtotal * percentage) / 100);

  const findTierIndex = (subtotal) =>
    tiers.reduce((found, tier, index) => (subtotal >= tier.threshold ? index : found), -1);

  const tierHasGifts = (index) => (tiers[index]?.gift_options ?? []).length > 0;

  const ordinal = (index) => String(index + 1).padStart(2, "0");

  // "5% OFF UNLOCKED" splits so the state word can carry its own colour.
  const splitHeadline = (headline) => {
    const match = String(headline ?? "").match(/^(.*\S)\s+(UNLOCKED)$/i);
    return match ? { value: match[1], state: match[2] } : { value: String(headline ?? ""), state: "" };
  };

  const createLadderView = ({ countsBuilder }) => {
    // The ladder is the shop's and never changes, so it is printed once in the
    // layout and read once here. What each bar was set up with does change from
    // page to page, and pages swap without reloading, so those are re-read every
    // time a bar wakes up rather than frozen at the first one.
    let hotThreshold = 0;
    let promoMode = "none";
    let campaignDeadline = null;
    let overrides = {};
    let offerWhenEmpty = false;

    const activeBuilder = () =>
      countsBuilder && builderElement && document.contains(builderElement) ? builder : null;

    const getPageSubtotal = (keep, getBuilderSubtotal) => {
      if (!countsBuilder) return 0;
      const currentBuilder = activeBuilder();
      if (currentBuilder) return getBuilderSubtotal(currentBuilder);
      return sumPageProductLines(keep);
    };

    const getSubtotal = () =>
      cartSubtotal() +
      getPageSubtotal(countsTowardThresholds, (currentBuilder) => currentBuilder.getThresholdSubtotal());

    const getDiscountBase = () =>
      cartDiscountBase() +
      getPageSubtotal(countsTowardDiscount, (currentBuilder) => currentBuilder.getDiscountSubtotal());

    // Wording the merchant typed on the builder wins; anything they cleared falls
    // back to the theme's own translations, which is all a bar standing on its own
    // has to go on.
    const wordingFor = (key) =>
      overrides[key] || window.translations?.bundle_configurator?.[key] || "";

    const text = (key, replacements) =>
      Object.entries(replacements ?? {}).reduce(
        (line, [token, value]) => line.replace(`[${token}]`, value),
        wordingFor(key)
      );

    // Liquid's t filter escapes HTML, so the translations stay plain text and the
    // highlighted parts get their markup here instead.
    const accentText = (key, replacements, accentTokens) =>
      Object.entries(replacements ?? {}).reduce(
        (line, [token, value]) =>
          line.replace(`[${token}]`, accentTokens.includes(token) ? `<span class="accent">${value}</span>` : value),
        wordingFor(key)
      );

    // What a tier actually hands over, read off its own settings rather than off
    // its place in the ladder. A tier can carry more than one mechanic at once —
    // the live ladder has a tier that gives 5% and a free item together — so the
    // name is built from every part it holds.
    const giftName = () => text("gift_tier_name");

    const tierGives = (index) => {
      const tier = tiers[index];
      if (!tier) return [];

      const parts = [];
      const percent = percentageOf(index);
      if (percent > 0) parts.push(text("deal_percent", { percent }));
      if (isFreeShippingValue(tier.percentage)) parts.push(text("deal_free_shipping"));
      if (tierHasGifts(index)) parts.push(giftName());
      return parts;
    };

    const tierName = (index) => {
      const parts = tierGives(index);
      return parts.length ? parts.join(" + ") : text("tier_name_fallback");
    };

    const state = window.Alpine.reactive({
      subtotal: 0,
      discount_base: 0,
      tier_index: -1,
      discount_percentage: 0,
      is_free_shipping: false,
      shipping_value: 0,
      gift_value: 0,
      total_saving: 0,
      total_saving_text: "",
      free_ids: [],
      gift_picks: {},
      gift_tiers_view: [],
      headline: "",
      headline_value: "",
      headline_state: "",
      subheadline: "",
      progress_percent: 0,
      tier_nodes: [],
      tier_labels: [],
      gift_drawer_open: false,
      gift_drawer_name: "",
      picking_gift_id: null,
      is_hot: false,
      is_max: false,
      is_empty: false,
      campaign_live: true,
      campaign_ends_label: "",
    });

    const getCartLineUnitPrice = (line) => (line.original_line_price ?? 0) / (line.quantity || SINGLE_UNIT);

    const buildCartGiftCandidates = () =>
      cartLines()
        .filter((line) => giftOptionsById.has(line.product_id))
        .map((line) => ({
          id: line.product_id,
          price: getCartLineUnitPrice(line),
          in_cart: true,
          line_key: line.key,
          is_chosen: isChosenGiftLine(line),
        }));

    const buildPageGiftCandidates = () => {
      if (!countsBuilder) return [];
      const quantity = getPageProductState()?.quantity || SINGLE_UNIT;
      return getPageProductLines()
        .filter((line) => giftOptionsById.has(line.product_id))
        .map((line) => ({ id: line.product_id, price: line.price / quantity, in_cart: false, is_chosen: false }));
    };

    const buildStagedGiftCandidates = () => {
      const currentBuilder = activeBuilder();
      if (!currentBuilder) return buildPageGiftCandidates();
      const stagedPickIds = Object.values(currentBuilder.getStagedGiftPicks() ?? {}).filter(Boolean);
      return currentBuilder
        .getStagedProducts()
        .filter((stagedProduct) => giftOptionsById.has(stagedProduct.id))
        .map((stagedProduct) => ({
          id: stagedProduct.id,
          price: stagedProduct.price,
          in_cart: false,
          is_chosen: stagedPickIds.includes(stagedProduct.id),
        }));
    };

    const findMostExpensiveCandidate = (candidates) =>
      candidates.reduce((best, candidate) => (!best || candidate.price > best.price ? candidate : best), null);

    const isTierReached = (tierIndex, subtotal) => tiers[tierIndex] !== undefined && subtotal >= tiers[tierIndex].threshold;

    const findGiftPicks = (subtotal) => {
      const picks = {};
      const candidates = [...buildCartGiftCandidates(), ...buildStagedGiftCandidates()];
      const claimedCandidates = new Set();

      tiers.forEach((tier, index) => {
        if (!isTierReached(index, subtotal)) return;
        const optionIds = (tier.gift_options ?? []).map((option) => option.id);
        const tierCandidates = candidates.filter(
          (candidate) => optionIds.includes(candidate.id) && !claimedCandidates.has(candidate)
        );
        const chosenCandidates = tierCandidates.filter((candidate) => candidate.is_chosen);
        const freeCandidate = findMostExpensiveCandidate(chosenCandidates.length ? chosenCandidates : tierCandidates);
        if (!freeCandidate) return;

        claimedCandidates.add(freeCandidate);
        picks[index] = freeCandidate;
      });

      return picks;
    };

    const findOutrankedStagedPickTiers = (subtotal) => {
      const stagedPicks = activeBuilder()?.getStagedGiftPicks() ?? {};
      const stagedFreeIds = Object.values(state.gift_picks)
        .filter((pick) => !pick.in_cart)
        .map((pick) => pick.id);
      return Object.entries(stagedPicks)
        .filter(
          ([tierIndex, productId]) =>
            productId && isTierReached(Number(tierIndex), subtotal) && !stagedFreeIds.includes(productId)
        )
        .map(([tierIndex]) => Number(tierIndex));
    };

    const findReachedGiftTiers = (subtotal) =>
      tiers.map((tier, index) => index).filter((index) => tierHasGifts(index) && isTierReached(index, subtotal));

    const findMostValuableFreeOption = (tierIndex) => {
      const takenIds = Object.values(state.gift_picks).map((pick) => pick.id);
      const freeOptions = (tiers[tierIndex]?.gift_options ?? []).filter(
        (option) => option.available !== false && !takenIds.includes(option.id)
      );
      return findMostExpensiveCandidate(freeOptions);
    };

    const stageMostValuableGifts = (subtotal) => {
      const currentBuilder = activeBuilder();
      if (!currentBuilder?.getReleasedGiftTiers) return false;
      const reachedGiftTiers = findReachedGiftTiers(subtotal);
      currentBuilder.keepReleasedGiftTiers(reachedGiftTiers);
      const releasedTiers = currentBuilder.getReleasedGiftTiers();
      const emptyTier = reachedGiftTiers.find(
        (tierIndex) => !state.gift_picks[tierIndex] && !releasedTiers.includes(tierIndex)
      );
      if (emptyTier === undefined) return false;
      const option = findMostValuableFreeOption(emptyTier);
      if (!option) return false;
      currentBuilder.setStagedGiftPick(emptyTier, option.id);
      return true;
    };

    // Every unlocked gift tier stays on screen and stays re-pickable. Showing only
    // the tier that still needs a choice made the block vanish the moment it was
    // answered, so a buyer could not change their mind.
    const buildGiftTiers = (subtotal) =>
      tiers
        .map((tier, index) => ({ tier, index }))
        .filter(({ tier }) => (tier.gift_options ?? []).length && subtotal >= tier.threshold)
        .map(({ tier, index }) => {
          const pickedId = state.gift_picks[index]?.id ?? null;
          return {
            index,
            label: text("tier_head", { ordinal: ordinal(index) }),
            name: tierName(index),
            picked_id: pickedId,
            picked_title: pickedId ? giftOptionsById.get(pickedId)?.title ?? "" : "",
            options: (tier.gift_options ?? []).map((option) => {
              const picked = pickedId === option.id;
              // taken by another tier, so this one cannot have it as well
              const claimedElsewhere = state.free_ids.includes(option.id) && !picked;
              return {
                id: option.id,
                title: option.title,
                image: option.image,
                image_srcset: option.image_srcset ?? "",
                tier_label: showsGiftTierBadge ? text("tier_head", { ordinal: ordinal(index) }) : "",
                value_text: text("gift_value", { amount: money(option.price) }),
                is_picked: picked,
                claimed_elsewhere: claimedElsewhere,
                pill: picked
                  ? text("gift_pill_free")
                  : claimedElsewhere
                    ? text("gift_pill_claimed")
                    : text("gift_pill_make_free"),
              };
            }),
          };
        });

    // Every tier owns an equal slice of the bar, so the dots sit evenly however far
    // apart the thresholds are. Spacing them by money made a cheap first tier hug
    // the left edge and the rest bunch up at the end.
    const isReached = (tier, subtotal) => !state.is_empty && subtotal >= tier.threshold;

    const buildProgress = (subtotal) => {
      const tierCount = tiers.length;
      if (!tierCount) {
        state.progress_percent = 0;
        state.tier_nodes = [];
        state.tier_labels = [];
        return;
      }

      const isBaseTier = (tier) => tier.threshold <= 0;
      const baseTierCount = tiers.filter(isBaseTier).length;
      const steppedTiers = tiers.filter((tier) => !isBaseTier(tier));
      const segment = steppedTiers.length ? 100 / steppedTiers.length : 0;
      const clearedCount = steppedTiers.filter((tier) => subtotal >= tier.threshold).length;
      let percent = steppedTiers.length ? clearedCount * segment : 100;

      if (clearedCount < steppedTiers.length) {
        const from = clearedCount > 0 ? steppedTiers[clearedCount - 1].threshold : 0;
        const to = steppedTiers[clearedCount].threshold;
        const span = to - from;
        if (span > 0) {
          const travelled = Math.min(Math.max((subtotal - from) / span, 0), 1);
          percent += travelled * segment;
        }
      }

      state.progress_percent = state.is_empty ? 0 : Math.min(percent, 100);
      state.tier_nodes = tiers.map((tier, index) => ({
        index,
        left: isBaseTier(tier) ? 0 : (index - baseTierCount + 1) * segment,
        is_hit: isReached(tier, subtotal),
      }));

      const nextIndex = tiers.findIndex((tier) => !isReached(tier, subtotal));
      state.tier_labels = tiers.map((tier, index) => ({
        index,
        tier: text("chip_tier", { ordinal: ordinal(index) }),
        name: tierName(index),
        is_hit: isReached(tier, subtotal),
        is_next: index === nextIndex,
      }));
    };

    // Everything earned, once the ladder is finished. Each mechanic says it in its
    // own words: a shipping deal has to name what the freight was worth, and a
    // straight percentage has nothing to hand over to put a value on.
    const maxSubheadline = () => {
      switch (promoMode) {
        case "percent":
          return accentText(
            "sub_max_percent_gift",
            { percent: state.discount_percentage, amount: money(state.gift_value) },
            ["amount"]
          );
        case "percentOnly":
          return accentText(
            "sub_max_percent_only",
            { deal: text("deal_percent", { percent: state.discount_percentage }) },
            ["deal"]
          );
        case "shipping":
          return accentText("sub_max_shipping_gift", { amount: money(state.gift_value) }, [
            "amount",
          ]);
        case "shipOnly":
          return accentText("sub_max_shipping_only", { amount: money(state.shipping_value) }, [
            "amount",
          ]);
        default:
          return state.gift_value > 0
            ? accentText("sub_max", { amount: money(state.gift_value) }, ["amount"])
            : accentText("sub_max_saving", { amount: money(state.total_saving) }, ["amount"]);
      }
    };

    const offerDeal = (index) => {
      const parts = tierGives(index).map((part) => (part === giftName() ? text("offer_gift") : part));
      return (parts.length ? parts.join(" + ") : text("tier_name_fallback")).toLowerCase();
    };

    const offerStep = (tier, index) =>
      tier.threshold > 0
        ? accentText("offer_tier", { deal: offerDeal(index), amount: money(tier.threshold) }, ["deal"])
        : accentText("offer_tier_sitewide", { deal: offerDeal(index) }, ["deal"]);

    const offerSentence = () => {
      const steps = tiers.map(offerStep);
      const lastStep = steps.pop();
      if (!steps.length) return lastStep;
      return `${steps.join(", ")}, ${text("offer_then", { step: lastStep })}`;
    };

    const findUnpickedTierIndex = (subtotal) =>
      tiers.findIndex(
        (tier, index) => subtotal >= tier.threshold && tierHasGifts(index) && !state.gift_picks[index]
      );

    const buildHeadline = (subtotal) => {
      const unlockedCount = tiers.filter((tier) => subtotal >= tier.threshold).length;
      const unpickedIndex = findUnpickedTierIndex(subtotal);

      // No ladder at all is No Sale, not a finished one. Without this the empty
      // list reads as every tier cleared and the bar announces a deal that was
      // never on offer.
      if (!tiers.length) {
        state.is_hot = false;
        state.is_max = false;
        state.headline = "";
        state.subheadline = "";
        return;
      }

      if (state.is_empty) {
        state.is_hot = false;
        state.is_max = false;
        state.headline = "";
        state.subheadline = offerSentence();
        return;
      }

      if (unpickedIndex >= 0) {
        state.is_hot = true;
        state.is_max = false;
        state.headline = text("headline_unlocked", { tier: ordinal(unpickedIndex) });
        state.subheadline = accentText("sub_pick_now", { tier: giftName().toLowerCase() }, ["tier"]);
        return;
      }

      if (unlockedCount === tiers.length) {
        state.is_hot = false;
        state.is_max = true;
        state.headline = text("headline_max");
        state.subheadline = maxSubheadline();
        return;
      }

      const nextIndex = tiers.findIndex((tier) => subtotal < tier.threshold);
      const nextTier = tiers[nextIndex];
      const remaining = nextTier.threshold - subtotal;
      const topValue = Math.max(...(nextTier.gift_options ?? []).map((option) => option.price), 0);
      const veryHot = remaining <= hotThreshold;

      state.is_max = false;
      state.is_hot = remaining <= hotThreshold * 2;
      state.headline = veryHot
        ? text(`headline_hot_${nextIndex + 1}`) || text("headline_hot_fallback")
        : text(`headline_far_${nextIndex + 1}`) || text("headline_far_fallback");
      // What the next tier is worth, built apart and dropped in. A tier handing
      // over an item is worth up to the dearest of them; a shipping tier is worth
      // its share of what the order would come to once the tier is reached. A tier
      // that only moves the percentage has nothing to put a figure on.
      // A tier handing over an item is worth up to the dearest of them, and saying
      // so is what makes the next step worth taking. A free shipping tier carries
      // no such figure: what the freight would have cost is a share of an order
      // the buyer has not finished, so the line names the offer and stops there.
      let worth = "";
      if (tierHasGifts(nextIndex)) {
        worth = accentText("sub_worth", { value: money(topValue) }, ["value"]);
      }
      worth = spaceBefore(worth);
      state.subheadline = accentText(
        veryHot ? "sub_almost" : "sub_away",
        {
          amount: money(remaining),
          tier: tierName(nextIndex).toLowerCase(),
          worth,
        },
        ["amount", "tier"]
      );
    };

    const recalculate = () => {
      const subtotal = getSubtotal();

      state.subtotal = subtotal;
      state.discount_base = getDiscountBase();
      state.is_empty = offerWhenEmpty && subtotal <= 0;
      state.tier_index = findTierIndex(subtotal);
      state.discount_percentage = bestPercentage(state.tier_index);
      state.is_free_shipping = hasFreeShipping(state.tier_index);
      state.shipping_value = state.is_free_shipping ? shippingValueFor(subtotal) : 0;

      state.gift_picks = findGiftPicks(subtotal);
      const outrankedStagedPickTiers = findOutrankedStagedPickTiers(subtotal);
      if (outrankedStagedPickTiers.length) {
        activeBuilder().dropStagedGiftPicks(outrankedStagedPickTiers);
        recalculate();
        return;
      }
      if (stageMostValuableGifts(subtotal)) {
        recalculate();
        return;
      }

      const freePicks = Object.values(state.gift_picks);
      state.free_ids = freePicks.map((pick) => pick.id);
      state.gift_value = freePicks.reduce((sum, pick) => sum + pick.price, 0);
      state.discount_base = Math.max(
        state.discount_base -
          freePicks
            .filter((pick) => countsTowardDiscount(pick.id))
            .reduce((sum, pick) => sum + pick.price, 0),
        0
      );

      state.total_saving =
        state.gift_value +
        state.shipping_value +
        percentSavingFor(state.discount_base, state.discount_percentage);

      // Freight is a share of the order rather than a quote from a carrier, so a
      // saving that leans on it is an estimate and has to say so wherever it is
      // printed. Without shipping in the sum every figure is exact.
      state.total_saving_text = state.is_free_shipping
        ? text("deal_shipping_estimate", { amount: money(state.total_saving) })
        : money(state.total_saving);

      state.gift_tiers_view = buildGiftTiers(subtotal);
      buildHeadline(subtotal);

      const headlineParts = splitHeadline(state.headline);
      state.headline_value = headlineParts.value;
      state.headline_state = headlineParts.state;
      buildProgress(subtotal);

      // every pick, so the closed drawer still says what was chosen
      state.gift_drawer_name = state.gift_tiers_view
        .map((giftTier) => giftTier.picked_title)
        .filter(Boolean)
        .join(" · ");

      syncGiftDrawer();
      activeBuilder()?.onLadderChange();
    };

    let openedGiftTierCount = 0;

    const hasUnansweredGiftTier = () => state.gift_tiers_view.some((giftTier) => !giftTier.picked_id);

    const syncGiftDrawer = () => {
      const earned = state.gift_tiers_view;

      if (!earned.length) {
        openedGiftTierCount = 0;
        state.gift_drawer_open = false;
        return;
      }

      if (earned.length > openedGiftTierCount && hasUnansweredGiftTier()) {
        state.gift_drawer_open = true;
        showGiftDrawer();
      }

      openedGiftTierCount = earned.length;
    };

    const closeGiftDrawerWhenAnswered = () => {
      if (!hasUnansweredGiftTier()) state.gift_drawer_open = false;
    };

    // What the bar on this page was set up with. A page swap leaves this store
    // standing, so the wording and the campaign have to be taken again from the
    // bar that just arrived rather than kept from the first one ever seen.
    const adoptSettings = (root) => {
      const settings =
        utils.JSONParse(root.querySelector("[data-bundle-progress-settings]")?.innerHTML) ?? {};
      hotThreshold = toLocalCurrency(settings.hot_threshold ?? 0);
      promoMode = settings.promo_mode ?? "none";
      overrides = settings.text ?? {};
      offerWhenEmpty = settings.offer_when_empty === true;

      const endsAt = new Date(settings.campaign_ends_at ?? "");
      campaignDeadline = isNaN(endsAt.getTime()) ? null : endsAt.getTime();
      state.campaign_live = !campaignDeadline || campaignDeadline > Date.now();
    };

    // Whichever bars are on the page right now. A bar from a page the buyer has
    // left is still in the list — pages swap without reloading here — so the ones
    // that have gone are filtered out at the moment of asking.
    let barRoots = [];

    const registerBar = (root) => {
      barRoots = barRoots.filter((element) => document.contains(element));
      if (!barRoots.includes(root)) barRoots.push(root);
    };

    const attachedBars = () => barRoots.filter((element) => document.contains(element));

    const showGiftDrawer = () => {
      requestAnimationFrame(() => {
        const drawer = attachedBars()
          .map((root) => root.querySelector(".gift-drawer"))
          .find(Boolean);
        if (!drawer) return;

        const card = drawer.querySelector(".gift-tier.unpicked") ?? drawer.querySelector("[data-gift-tier-card]");
        if (!card) return;
        card.classList.remove("nudge");
        void card.offsetWidth;
        card.classList.add("nudge");
        setTimeout(() => card.classList.remove("nudge"), 900);
      });
    };

    const findCartLineToKeep = (productId) => {
      const productLines = cartLines().filter((line) => line.product_id === productId && line.quantity > 0);
      return productLines.find((line) => !isChosenGiftLine(line)) ?? productLines[0] ?? null;
    };

    const findCartLineByKey = (lineKey) => cartLines().find((line) => line.key === lineKey) ?? null;

    const releaseCartGift = async (pick) => {
      const line = findCartLineByKey(pick.line_key);
      if (!line || !isChosenGiftLine(line)) return;
      if (isAddedOnlyAsGift(line)) {
        await _cart.change({ id: line.key, quantity: 0 });
        return;
      }
      await _cart.change({ id: line.key, quantity: line.quantity, properties: removeGiftChoice(line.properties) });
    };

    const keepCartLineAsGift = async (line) => {
      if (isChosenGiftLine(line)) return;
      await _cart.change({
        id: line.key,
        quantity: line.quantity,
        properties: { ...line.properties, ...giftChoiceProperties(true) },
      });
    };

    const addGiftToCart = (productId, variantId) =>
      _cart.add({
        items: [
          {
            id: variantId,
            quantity: SINGLE_UNIT,
            properties: { ...bundleProperties(productId), ...giftChoiceProperties(false) },
          },
        ],
      });

    const releaseStagedGift = (currentBuilder, pick) => {
      if (pick && !pick.in_cart) currentBuilder?.forgetStagedGiftPick(pick.id);
    };

    const pickGift = async (tierIndex, productId) => {
      if (state.picking_gift_id) return;
      if (state.subtotal < tiers[tierIndex]?.threshold) return;
      const current = state.gift_picks[tierIndex];
      if (current?.id === productId) return;

      const currentBuilder = activeBuilder();
      const variantId = giftOptionsById.get(productId)?.variant_id;
      const lineToKeep = findCartLineToKeep(productId);
      if (!lineToKeep && !currentBuilder && !variantId) return;

      const needsCartRequest = !!current?.in_cart || !!lineToKeep || !currentBuilder;
      if (!needsCartRequest) {
        releaseStagedGift(currentBuilder, current);
        currentBuilder.setStagedGiftPick(tierIndex, productId);
        recalculate();
        closeGiftDrawerWhenAnswered();
        return;
      }

      state.picking_gift_id = productId;
      try {
        if (current?.in_cart) await releaseCartGift(current);
        releaseStagedGift(currentBuilder, current);
        const freshLineToKeep = findCartLineToKeep(productId);
        if (freshLineToKeep) {
          await keepCartLineAsGift(freshLineToKeep);
        } else if (currentBuilder) {
          currentBuilder.setStagedGiftPick(tierIndex, productId);
        } else {
          await addGiftToCart(productId, variantId);
        }
      } finally {
        state.picking_gift_id = null;
        recalculate();
        closeGiftDrawerWhenAnswered();
      }
    };

    const toggleGiftDrawer = () => {
      state.gift_drawer_open = !state.gift_drawer_open;
    };

    const escapeMarkup = (value) =>
      String(value).replace(/[&<>]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[character]);

    const buildDealParts = () => {
      const parts = [];
      if (state.discount_percentage > 0) {
        parts.push({ kind: "val", text: text("deal_percent", { percent: state.discount_percentage }) });
      }
      if (state.is_free_shipping) {
        parts.push({ kind: "add", text: text("deal_free_shipping"), estimate: state.shipping_value });
      }
      state.gift_tiers_view.forEach((giftTier) => {
        if (giftTier.picked_title) {
          parts.push({ kind: "add", text: text("deal_free_item", { item: giftTier.picked_title }) });
        }
      });
      return parts;
    };

    const renderDealHtml = (parts) =>
      parts
        .map((part) => {
          const estimate = part.estimate
            ? `<i class="dl-est">${escapeMarkup(text("deal_shipping_estimate", { amount: money(part.estimate) }))}</i>`
            : "";
          return `<span class="dl-${part.kind}">${escapeMarkup(part.text)}${estimate}</span>`;
        })
        .join('<span class="dl-plus">+</span>');

    // A sale that has run out can never be left up by accident: the strip goes and
    // the campaign skin comes off, the way the working file repaints on expiry.
    // Every bar hands its root over and one clock repaints them all. Roots from
    // pages the buyer has left are dropped as the clock passes them, so a long
    // session does not collect a bar for every page it has been through.
    let campaignTicking = false;

    const startCampaignCountdown = (root) => {
      registerBar(root);
      if (!campaignDeadline || campaignTicking) return;
      campaignTicking = true;

      const pad = (value) => String(value).padStart(2, "0");

      const tick = () => {
        const remaining = campaignDeadline - Date.now();
        if (remaining <= 0) {
          state.campaign_live = false;
          state.campaign_ends_label = "";
          campaignTicking = false;
          attachedBars().forEach((element) => {
            element.setAttribute("data-sale", "none");
            // The skin also lives in inline custom properties, and a few rules
            // read them without asking whether a sale is on. Left behind, they
            // keep the campaign colour on the unlocked word and the saving badge.
            [...element.style]
              .filter((property) => property.startsWith("--cmp-"))
              .forEach((property) => element.style.removeProperty(property));
          });
          return;
        }
        const totalMinutes = Math.floor(remaining / 60000);
        const days = Math.floor(totalMinutes / 1440);
        const hours = Math.floor((totalMinutes % 1440) / 60);
        const minutes = totalMinutes % 60;
        const seconds = Math.floor(remaining / 1000) % 60;
        const clock = days > 0
          ? `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
          : `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
        state.campaign_ends_label = text("ends_in", { time: clock });
        setTimeout(tick, 1000);
      };

      tick();
    };

    // The cart can change without this page asking: a line removed in the drawer,
    // a quantity stepped up. Watching the lines themselves catches all of it,
    // where the add event only fires for what this page put in — and it fires
    // before the cart has finished reloading, so it arrives too early to read.
    // The work is queued so it runs outside the effect, or writing the tier state
    // here would set the same effect off again.
    let lastCartSignature = null;
    window.Alpine.effect(() => {
      const signature = cartLines()
        .map((line) => `${line.product_id}x${line.quantity}@${line.original_line_price}`)
        .join("|");
      if (signature === lastCartSignature) return;
      lastCartSignature = signature;
      queueMicrotask(recalculate);
    });

    let lastPageProductSignature = null;
    if (countsBuilder) {
      window.Alpine.effect(() => {
        const signature = getPageProductLines()
          .map((line) => `${line.variant_id}@${line.price}`)
          .join("|");
        if (signature === lastPageProductSignature) return;
        lastPageProductSignature = signature;
        queueMicrotask(recalculate);
      });
    }

    recalculate();
    return {
      state,
      text,
      money,
      tierName,
      pickGift,
      toggleGiftDrawer,
      buildDealParts,
      renderDealHtml,
      showGiftDrawer,
      registerBar,
      recalculate,
      adoptSettings,
      startCampaignCountdown,
    };
  };

  const pageView = createLadderView({ countsBuilder: true });
  let cartView = null;

  const getView = (scope) => {
    if (scope !== "cart") return pageView;
    if (!cartView) cartView = createLadderView({ countsBuilder: false });
    return cartView;
  };
  return {
    state: pageView.state,
    tiers,
    text: pageView.text,
    thresholdProductIds,
    giftOptionsById,
    isFreeShippingValue,
    takesPartInTheLadder,
    countsTowardThresholds,
    countsTowardDiscount,
    bundleProperties,
    giftChoiceProperties,
    percentageOf,
    percentSavingFor,
    money,
    tierHasGifts,
    tierName: pageView.tierName,
    ordinal,
    shippingValueFor,
    pickGift: pageView.pickGift,
    toggleGiftDrawer: pageView.toggleGiftDrawer,
    buildDealParts: pageView.buildDealParts,
    renderDealHtml: pageView.renderDealHtml,
    showGiftDrawer: pageView.showGiftDrawer,
    registerBar: pageView.registerBar,
    recalculate: pageView.recalculate,
    adoptSettings: pageView.adoptSettings,
    startCampaignCountdown: pageView.startCampaignCountdown,
    getView,
    // The builder hands itself over so its staged lines join the sum and its
    // gift picks join the ladder's. Only one builder can be on a page.
    getProductStatePrices: (productState) => {
      const lines = buildProductStateLines(productState, SINGLE_UNIT);
      const fullPrice = lines.reduce((sum, line) => sum + line.price, 0);
      const freeStagedIds = Object.values(pageView.state.gift_picks)
        .filter((pick) => !pick.in_cart)
        .map((pick) => pick.id);
      const isFreeLine = (line) => freeStagedIds.includes(line.product_id);
      const giftValue = lines.filter(isFreeLine).reduce((sum, line) => sum + line.price, 0);
      const percentSaving = percentSavingFor(
        lines
          .filter((line) => countsTowardDiscount(line.product_id) && !isFreeLine(line))
          .reduce((sum, line) => sum + line.price, 0),
        pageView.state.discount_percentage
      );
      return { fullPrice, saving: giftValue + percentSaving };
    },
    connectBuilder: (connected, element) => {
      builder = connected;
      builderElement = element;
      pageView.recalculate();
    },
  };
};

window._sections = window._sections || {};
window._stores = window._stores || {};

// One ladder for the page, built the first time anything asks for it. The bar
// and the builder both go through here, which is what keeps the two of them
// counting the same money.
const getBundleLadder = () => {
  if (!window._stores.bundleLadder) window._stores.bundleLadder = buildBundleLadder();
  return window._stores.bundleLadder;
};

const readBarScope = ($el) =>
  utils.JSONParse($el.querySelector("[data-bundle-progress-settings]")?.innerHTML)?.scope ?? "page";

const initBundleProgress = ($el) => {
  const view = getBundleLadder().getView(readBarScope($el));
  view.registerBar($el);
  view.adoptSettings($el);
  view.startCampaignCountdown($el);
  view.recalculate();

  return {
    bundle: view.state,
    money: view.money,
    pickGift: view.pickGift,
    toggleGiftDrawer: view.toggleGiftDrawer,
  };
};

window._sections["initBundleProgress"] = initBundleProgress;
window._sections["getBundleLadder"] = getBundleLadder;

const STRUCK_PRICE_STYLE = "text-decoration: line-through; opacity: 0.5;";

const formatLadderPrice = (productState) => {
  const { fullPrice, saving } = getBundleLadder().getProductStatePrices(productState);
  if (productState?.selected_selling_plan) return utils.formatMoney(productState?.total_price ?? 0);
  if (saving <= 0) return utils.formatMoney(fullPrice);
  return `<span style="${STRUCK_PRICE_STYLE}">${utils.formatMoney(fullPrice)}</span> ${utils.formatMoney(fullPrice - saving)}`;
};

window._sections["formatLadderPrice"] = formatLadderPrice;

const isProductFormBlocked = (productState) =>
  !!productState?.isAdding ||
  (productState?.disable_add_to_cart?.size ?? 0) > 0 ||
  (productState?.required_combine_items?.length ?? 0) < (productState?.required_combine_quantity ?? 0);

const getProductDeal = (productState) => {
  const ladderStore = getBundleLadder();
  const { fullPrice, saving } = ladderStore.getProductStatePrices(productState);
  const isActive = !productState?.selected_selling_plan && saving > 0;
  return {
    is_active: isActive,
    is_bare: !isActive,
    label_html: isActive ? ladderStore.renderDealHtml(ladderStore.buildDealParts()) : "",
    saving_text: ladderStore.state.total_saving_text,
    retail_text: ladderStore.money(fullPrice),
    button_text: ladderStore.text("cta_add"),
    full_price_text: ladderStore.money(fullPrice),
    net_price_text: ladderStore.money(fullPrice - saving),
    shows_full_price: isActive,
    shows_net_price: true,
    is_disabled: isProductFormBlocked(productState),
    is_loading: !!productState?.isAdding,
  };
};

window._sections["getProductDeal"] = getProductDeal;
